import { useEffect, useState, useRef } from "react";
import { useOnlineStatus } from "../hooks/useOnlineStatus";

function AnimatedWifiOff() {
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Outer arc */}
      <path
        d="M8 24C14.4 17.6 23.2 14 32 14C40.8 14 49.6 17.6 56 24"
        stroke="#2d3a2e"
        strokeWidth="4"
        strokeLinecap="round"
        className="wifi-arc wifi-arc-3"
      />
      {/* Mid arc */}
      <path
        d="M15 31C19.4 26.6 25.6 24 32 24C38.4 24 44.6 26.6 49 31"
        stroke="#2d3a2e"
        strokeWidth="4"
        strokeLinecap="round"
        className="wifi-arc wifi-arc-2"
      />
      {/* Inner arc */}
      <path
        d="M22 38C24.8 35.2 28.4 34 32 34C35.6 34 39.2 35.2 42 38"
        stroke="#2d3a2e"
        strokeWidth="4"
        strokeLinecap="round"
        className="wifi-arc wifi-arc-1"
      />
      {/* Dot */}
      <circle cx="32" cy="48" r="3.5" fill="#2d3a2e" className="wifi-dot" />
      {/* Strike-through line */}
      <line
        x1="12" y1="12" x2="52" y2="52"
        stroke="#ef4444"
        strokeWidth="4"
        strokeLinecap="round"
        className="wifi-strike"
      />
    </svg>
  );
}

// Floating particle orbs for background atmosphere
function Orbs() {
  return (
    <div className="orbs-wrap" aria-hidden="true">
      <div className="orb orb-1" />
      <div className="orb orb-2" />
      <div className="orb orb-3" />
    </div>
  );
}

export default function OfflineScreen() {
  const isOnline = useOnlineStatus();
  const [phase, setPhase] = useState("hidden"); // hidden | entering | visible | leaving
  const [retrying, setRetrying] = useState(false);
  const [retrySuccess, setRetrySuccess] = useState(false);
  const wasOffline = useRef(false);

  useEffect(() => {
    if (!isOnline) {
      wasOffline.current = true;
      setRetrySuccess(false);
      setPhase("entering");
      setTimeout(() => setPhase("visible"), 50);
    } else if (wasOffline.current && phase !== "hidden") {
      // Coming back online
      setRetrySuccess(true);
      setTimeout(() => {
        setPhase("leaving");
        setTimeout(() => {
          setPhase("hidden");
          setRetrySuccess(false);
          wasOffline.current = false;
        }, 600);
      }, 1200);
    }
  }, [isOnline, phase]);

  const handleRetry = () => {
    setRetrying(true);
    setTimeout(() => {
      setRetrying(false);
      if (navigator.onLine) {
        setRetrySuccess(true);
        setTimeout(() => {
          setPhase("leaving");
          setTimeout(() => setPhase("hidden"), 600);
        }, 1000);
      }
      // If still offline, just stop spinning — user sees nothing changed
    }, 2000);
  };

  if (phase === "hidden") return null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,700;1,500&family=DM+Sans:wght@300;400;500&display=swap');

        /* ── Keyframes ── */
        @keyframes offlineIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes offlineOut {
          from { opacity: 1; }
          to   { opacity: 0; }
        }
        @keyframes cardRise {
          from { opacity: 0; transform: translateY(32px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
        @keyframes cardExit {
          from { opacity: 1; transform: translateY(0)    scale(1); }
          to   { opacity: 0; transform: translateY(-20px) scale(0.97); }
        }
        @keyframes wifiArcPulse {
          0%, 100% { opacity: 0.15; }
          50%       { opacity: 0.55; }
        }
        @keyframes strikeDraw {
          from { stroke-dashoffset: 80; }
          to   { stroke-dashoffset: 0; }
        }
        @keyframes dotBounce {
          0%, 100% { transform: scale(1); }
          50%       { transform: scale(1.4); }
        }
        @keyframes orbFloat1 {
          0%, 100% { transform: translate(0, 0)   scale(1);   }
          50%       { transform: translate(20px, -30px) scale(1.08); }
        }
        @keyframes orbFloat2 {
          0%, 100% { transform: translate(0, 0)    scale(1);   }
          50%       { transform: translate(-25px, 20px) scale(0.94); }
        }
        @keyframes orbFloat3 {
          0%, 100% { transform: translate(0, 0)   scale(1);   }
          40%       { transform: translate(15px, 25px) scale(1.05); }
        }
        @keyframes spinLoader {
          to { transform: rotate(360deg); }
        }
        @keyframes successScale {
          0%   { transform: scale(0); opacity: 0; }
          60%  { transform: scale(1.2); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes fadeStagger {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmerBtn {
          0%   { background-position: -200% center; }
          100% { background-position:  200% center; }
        }

        /* ── Root overlay ── */
        .os-root {
          position: fixed;
          inset: 0;
          z-index: 99997;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          background: #080f09;
        }
        .os-root.entering { animation: offlineIn 0.4s ease forwards; }
        .os-root.leaving  { animation: offlineOut 0.6s ease forwards; }

        /* ── Orbs ── */
        .orbs-wrap {
          position: absolute;
          inset: 0;
          pointer-events: none;
          overflow: hidden;
        }
        .orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(70px);
          opacity: 0.18;
        }
        .orb-1 {
          width: 340px; height: 340px;
          background: radial-gradient(circle, #1a5c2a, transparent 70%);
          top: -80px; left: -60px;
          animation: orbFloat1 9s ease-in-out infinite;
        }
        .orb-2 {
          width: 280px; height: 280px;
          background: radial-gradient(circle, #0e3d1a, transparent 70%);
          bottom: -60px; right: -40px;
          animation: orbFloat2 11s ease-in-out infinite;
        }
        .orb-3 {
          width: 200px; height: 200px;
          background: radial-gradient(circle, #ef444430, transparent 70%);
          top: 40%; right: 10%;
          animation: orbFloat3 13s ease-in-out infinite;
        }

        /* Subtle grid texture */
        .os-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
          background-size: 40px 40px;
          pointer-events: none;
        }

        /* ── Card ── */
        .os-card {
          position: relative;
          z-index: 1;
          width: min(380px, calc(100vw - 48px));
          background: rgba(16,26,17,0.85);
          border: 1px solid rgba(74,163,93,0.12);
          border-radius: 28px;
          padding: 40px 36px 36px;
          text-align: center;
          backdrop-filter: blur(24px);
          box-shadow:
            0 0 0 1px rgba(255,255,255,0.04),
            0 24px 64px rgba(0,0,0,0.5),
            0 4px 16px rgba(0,0,0,0.3);
        }
        .os-root.entering .os-card { animation: cardRise 0.55s cubic-bezier(0.22,1,0.36,1) 0.1s both; }
        .os-root.leaving  .os-card { animation: cardExit 0.5s ease forwards; }

        /* ── Logo mark ── */
        .os-logo-row {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-bottom: 32px;
          animation: fadeStagger 0.5s 0.25s ease both;
        }
        .os-logo-box {
          width: 36px; height: 36px;
          background: linear-gradient(135deg, #1a5c2a, #2d8a3e);
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 2px 12px rgba(26,92,42,0.4);
        }
        .os-brand {
          font-family: 'Playfair Display', serif;
          font-size: 16px;
          font-weight: 500;
          color: rgba(255,255,255,0.6);
          letter-spacing: 2px;
          text-transform: uppercase;
        }

        /* ── WiFi icon wrap ── */
        .os-icon-wrap {
          width: 96px; height: 96px;
          margin: 0 auto 28px;
          position: relative;
          animation: fadeStagger 0.5s 0.35s ease both;
        }
        .os-icon-ring {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 1px solid rgba(239,68,68,0.15);
          background: radial-gradient(circle at center, rgba(239,68,68,0.07), transparent 70%);
        }
        .os-icon-ring-2 {
          position: absolute;
          inset: -12px;
          border-radius: 50%;
          border: 1px solid rgba(239,68,68,0.07);
        }
        .os-icon-inner {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Wifi arc animations */
        .wifi-arc { stroke-dasharray: 60; }
        .wifi-arc-3 { animation: wifiArcPulse 2s 0s   ease-in-out infinite; }
        .wifi-arc-2 { animation: wifiArcPulse 2s 0.3s ease-in-out infinite; }
        .wifi-arc-1 { animation: wifiArcPulse 2s 0.6s ease-in-out infinite; }
        .wifi-dot   { animation: dotBounce 2s 0.9s ease-in-out infinite; }
        .wifi-strike {
          stroke-dasharray: 80;
          animation: strikeDraw 0.6s 0.3s cubic-bezier(0.22,1,0.36,1) both;
        }

        /* ── Text ── */
        .os-headline {
          font-family: 'Playfair Display', serif;
          font-size: 26px;
          font-weight: 700;
          color: #f0f5f1;
          line-height: 1.25;
          letter-spacing: -0.4px;
          margin-bottom: 10px;
          animation: fadeStagger 0.5s 0.45s ease both;
        }
        .os-sub {
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          font-weight: 400;
          color: rgba(255,255,255,0.35);
          line-height: 1.6;
          margin-bottom: 32px;
          animation: fadeStagger 0.5s 0.55s ease both;
        }

        /* ── Divider ── */
        .os-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(74,163,93,0.2), transparent);
          margin-bottom: 28px;
          animation: fadeStagger 0.5s 0.6s ease both;
        }

        /* ── Retry button ── */
        .os-retry-btn {
          width: 100%;
          padding: 15px 24px;
          border: none;
          border-radius: 14px;
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: transform 0.15s, box-shadow 0.15s, opacity 0.2s;
          animation: fadeStagger 0.5s 0.65s ease both;
          position: relative;
          overflow: hidden;
        }
        .os-retry-btn:not(:disabled) {
          background: linear-gradient(135deg, #1a5c2a 0%, #2d8a3e 60%, #3aad52 100%);
          background-size: 200% auto;
          color: #fff;
          box-shadow: 0 4px 20px rgba(26,92,42,0.45), 0 1px 3px rgba(0,0,0,0.2);
        }
        .os-retry-btn:not(:disabled):hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 28px rgba(26,92,42,0.55);
          animation: shimmerBtn 1.5s linear infinite, fadeStagger 0.5s 0.65s ease both;
        }
        .os-retry-btn:not(:disabled):active {
          transform: translateY(1px);
          box-shadow: 0 2px 10px rgba(26,92,42,0.3);
        }
        .os-retry-btn:disabled {
          background: rgba(255,255,255,0.06);
          color: rgba(255,255,255,0.3);
          cursor: not-allowed;
          box-shadow: none;
        }

        /* Spinner inside button */
        .os-spinner {
          width: 17px; height: 17px;
          border: 2px solid rgba(255,255,255,0.25);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spinLoader 0.7s linear infinite;
          flex-shrink: 0;
        }

        /* Success checkmark */
        .os-check {
          animation: successScale 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards;
        }

        /* ── Footer note ── */
        .os-note {
          margin-top: 16px;
          font-family: 'DM Sans', sans-serif;
          font-size: 11px;
          color: rgba(255,255,255,0.18);
          letter-spacing: 0.3px;
          animation: fadeStagger 0.5s 0.75s ease both;
        }
      `}</style>

      <div className={`os-root ${phase}`}>
        <Orbs />
        <div className="os-grid" />

        <div className="os-card">

          {/* Logo row */}
          <div className="os-logo-row">
            <div className="os-logo-box">
              <svg width="20" height="20" viewBox="0 0 28 28" fill="none">
                <path d="M14 5L22 22H6L14 5Z" stroke="white" strokeWidth="2" strokeLinejoin="round" fill="none"/>
                <path d="M10 16H18" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <span className="os-brand">Canluxa</span>
          </div>

          {/* WiFi off icon */}
          <div className="os-icon-wrap">
            <div className="os-icon-ring-2" />
            <div className="os-icon-ring" />
            <div className="os-icon-inner">
              <AnimatedWifiOff />
            </div>
          </div>

          {/* Headline + sub */}
          <div className="os-headline">
            {retrySuccess ? "You're back online!" : "No Connection"}
          </div>
          <div className="os-sub">
            {retrySuccess
              ? "Everything is loading back up for you."
              : "Check your Wi-Fi or mobile data\nand try again."
            }
          </div>

          <div className="os-divider" />

          {/* Retry button */}
          <button
            className="os-retry-btn"
            onClick={handleRetry}
            disabled={retrying || retrySuccess}
          >
            {retrySuccess ? (
              <>
                <svg className="os-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                Connected
              </>
            ) : retrying ? (
              <>
                <div className="os-spinner" />
                Checking connection…
              </>
            ) : (
              <>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 4v6h-6M1 20v-6h6"/>
                  <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
                </svg>
                Try Again
              </>
            )}
          </button>

          <div className="os-note">
            Canluxa · Valet Trash
          </div>

        </div>
      </div>
    </>
  );
}